import { apiClient } from './api';

/**
 * Sending a command to an aircraft (#578).
 *
 * The UI could not do this at all until now: grepping the whole frontend for
 * the commands endpoint found one comment and no calls. Everything that has
 * ever flown on this cluster was commanded by `fleetctl` or raw HTTP, which
 * means an operator recovering a drone needed a terminal (#556).
 */

/** The flight commands the panel offers, spelled as the API spells them. */
export type FlightCommand =
  | 'takeoff'
  | 'land'
  | 'return_to_base'
  | 'emergency_land'
  | 'fly_route';

/** What the API answers with when a command is queued. */
export interface CommandAccepted {
  id: string;
  status: string;
  command_type: string;
}

export const commandService = {
  /**
   * Queue a command for a drone.
   *
   * Resolves when the API has **accepted** the command, which is not the same
   * as the aircraft having acted on it — #556 is precisely the gap between
   * those two, where a command reached `failed` while the drone stayed
   * airborne. Callers must say "commanded", never "landed".
   */
  async send(
    droneId: string,
    commandType: FlightCommand,
    parameters: Record<string, unknown> = {},
    options: { acknowledgeUncheckedTerrain?: boolean } = {}
  ): Promise<CommandAccepted> {
    return apiClient.post<CommandAccepted>(
      `/api/v1/drones/${droneId}/commands`,
      {
        command_type: commandType,
        parameters,
        // A fly_route whose legs the ground check could not judge is queued
        // only on the pilot's word (#624).
        ...(options.acknowledgeUncheckedTerrain
          ? { acknowledge_unchecked_terrain: true }
          : {}),
      }
    );
  },
};
